import type { ImproveResumeLlmOutput } from "./schema";

/**
 * Deterministic checks over the LLM output — plan §4, rules R5/R7/R8.
 *
 * Pure functions, same shape as `sanitize.ts`: no LLM, no network, microseconds.
 * They exist because the prompt is a request, not a guarantee. Every rule here
 * corresponds to a failure the rewrite drafts actually produced:
 *
 *  - V1 the model reported keyword coverage it never wrote into a bullet.
 *  - V2 "GitHub Actions CI/CD pipeline" — two target terms glued into one
 *       adjective phrase, which an ATS keyed on "CI/CD" does not match.
 *  - V5 the same keyword repeated down one role to hit a count, which is what
 *       produced both the monotony and the nonsense bullet.
 *  - V6 a 4-month internship given six bullets.
 *
 * They never throw and never mutate. A streamed, half-arrived object yields no
 * issues rather than a crash — the user paid for the rewrite, not the receipt.
 */

export type ValidationIssue = {
  rule: "V1" | "V2" | "V5" | "V6" | "HS";
  severity: "error" | "warning";
  message: string;
  where?: string;
};

type Bullets = { section: "experience" | "projects"; item: number; bullet: number; text: string };

export function validateImproveResumeOutput(
  out: ImproveResumeLlmOutput,
  sourceText?: string,
): ValidationIssue[] {
  if (!out?.optimized_resume) return [];
  return [
    ...checkKeywordsPresent(out),
    ...checkTokenBoundaries(out),
    ...checkFrequency(out),
    ...checkBulletBudget(out),
    ...(sourceText ? checkHardStop(out, sourceText) : []),
  ];
}

/* ------------------------------------------------------------------ HS */

/**
 * HARD STOP — a keyword the source resume never mentions must not appear in a
 * bullet as if the candidate had used it.
 *
 * The prompt says this at every level, but a prompt is a request, not a
 * guarantee — the same reason `sanitize.ts` exists. Observed on the fixture
 * resume at *balanced*: the candidate's resume names no cloud vendor anywhere,
 * and the model still wrote "12+ cloud services on AWS". That is the one
 * category interview preparation cannot rescue: "walk me through your AWS
 * setup" ends the call.
 *
 * The rule needs no vendor list. `stated`, `implied` and `promoted` all assert
 * that the source contains the parent fact, so the word being absent from the
 * source makes the assertion false — an error. `correlated` and `practice`
 * legitimately coin words the source never used ("Agile", "sprint planning"),
 * so those surface as warnings for the defend cards to carry.
 *
 * Deliberately asymmetric with V1: this scans the SOURCE, not a bullet, and a
 * false positive here puts a red flag on a truthful line, which is worse than
 * missing one. So it uses `mentionedInSource` (lenient) rather than
 * `containsKeyword` (strict). The first cut used the strict matcher and accused
 * the model of inventing "Git" (source says "Git/GitHub"), "HTML" (source says
 * "HTML5") and "REST APIs" (source says "REST API Design").
 */
export function checkHardStop(out: ImproveResumeLlmOutput, sourceText: string): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const bullets = indexBullets(out);
  const haystack = normaliseSource(sourceText);

  for (const a of out.keyword_assignments ?? []) {
    if (!a?.keyword) continue;
    if (mentionedInSource(haystack, a.keyword)) continue;

    const ref = a.bullet_ref;
    if (!ref) continue;
    const placed = bullets.get(`${ref.section}/${ref.item}/${ref.bullet}`);
    if (!placed || !containsKeyword(placed.text, a.keyword)) continue;

    const asserted =
      a.derivation === "stated" || a.derivation === "implied" || a.derivation === "promoted";
    issues.push({
      rule: "HS",
      severity: asserted ? "error" : "warning",
      message: asserted
        ? `"${a.keyword}" is in a bullet as "${a.derivation}", but the original resume never mentions it`
        : `"${a.keyword}" is in a bullet but the original resume never mentions it — defend it or drop it`,
      where: `${ref.section}/${ref.item}/${ref.bullet}`,
    });
  }

  return issues;
}

/** Slashes, pipes and punctuation become spaces, so "Git/GitHub" yields "git github". */
function normaliseSource(text: string): string {
  return ` ${text.toLowerCase().replace(/[^a-z0-9+#]+/g, " ")} `;
}

/**
 * Lenient: the source counts as mentioning a keyword when ANY word of it turns
 * up as a prefix of a source word. "HTML" is found in "HTML5", "REST APIs" in
 * "REST API Design", "CI/CD pipelines" in "CI/CD".
 *
 * Also tries stripping a trailing 's' so "LLMs" matches "LLM" in source and
 * "REST APIs" matches "REST API Design". Same direction as the s? in keywordRegex.
 *
 * ponytail: substring-prefix matching, no stemmer. It errs toward silence, which
 * is the right direction for a rule whose false positive is an accusation. If a
 * real invented term ever slips through because one of its words happens to
 * appear elsewhere, tighten to "every word must be found" — not before.
 */
function mentionedInSource(haystack: string, keyword: string): boolean {
  const words = keyword
    .toLowerCase()
    .split(/[^a-z0-9+#]+/)
    .filter((w) => w.length > 1);
  if (words.length === 0) return true;
  return words.some(
    (w) =>
      haystack.includes(` ${w}`) || (w.endsWith("s") && haystack.includes(` ${w.slice(0, -1)}`)),
  );
}

/* ------------------------------------------------------------------ V1 */

/**
 * V1 — every keyword the model claims it placed must literally be in the bullet
 * it points at, case-insensitive, at a token boundary.
 *
 * Token boundary, not `\b`: `\bC#\b` never matches, because `#` is already a
 * non-word character so there is no word boundary after it. Same for `C++`,
 * `.NET`, `CI/CD`, `Node.js`. So the boundary is expressed as "not flanked by a
 * character that could continue the token" instead, which keeps "Java" out of
 * "JavaScript" and still finds "C#" in "with C#, .NET, and Docker".
 */
export function checkKeywordsPresent(out: ImproveResumeLlmOutput): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const bullets = indexBullets(out);

  for (const a of out.keyword_assignments ?? []) {
    const ref = a?.bullet_ref;
    if (!a?.keyword || !ref) continue;
    const key = `${ref.section}/${ref.item}/${ref.bullet}`;
    const text = bullets.get(key)?.text;
    if (text === undefined) {
      issues.push({
        rule: "V1",
        severity: "error",
        message: `assignment for "${a.keyword}" points at a bullet that does not exist`,
        where: key,
      });
      continue;
    }
    if (!containsKeyword(text, a.keyword)) {
      issues.push({
        rule: "V1",
        severity: "error",
        message: `"${a.keyword}" is assigned here but does not appear in the bullet`,
        where: key,
      });
    }
  }

  // The scorecard is the trust artifact — a keyword listed as found but absent
  // from every bullet is the single worst thing this product can ship.
  const all = [...bullets.values()].map((b) => b.text).join("\n");
  for (const f of out.coverage?.found ?? []) {
    if (!f?.keyword) continue;
    if (!containsKeyword(all, f.keyword)) {
      issues.push({
        rule: "V1",
        severity: "error",
        message: `coverage claims "${f.keyword}" but it is in no bullet`,
        where: "coverage.found",
      });
    }
  }
  return issues;
}

/** Case-insensitive, token-boundary containment. Safe for `C#`, `.NET`, `CI/CD`. */
export function containsKeyword(text: string, keyword: string): boolean {
  return keywordRegex(keyword).test(text);
}

function keywordRegex(keyword: string): RegExp {
  const k = escapeRegex(keyword.trim());
  // A token may not be continued by a letter, digit, `+`, `#`, or a `.`/`/`
  // followed by more token (so "Node.js" does not match inside "Node.jsx", and
  // "Java" does not match inside "JavaScript", but "C#," and ".NET." do match).
  const left = "(?<![A-Za-z0-9+#./-])";
  // ponytail: s? allows singular keyword to match its plain plural ("REST API"
  // matches "REST APIs"). The right boundary still rejects "s" followed by more
  // word chars, so "Java" cannot match inside "JavaScript" via this path.
  const right = "s?(?![A-Za-z0-9+#-]|[./][A-Za-z0-9])";
  return new RegExp(left + k + right, "i");
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/* ------------------------------------------------------------------ V2 */

/**
 * V2 — R7: a target term must stand as its own comma-delimited token, never be
 * swallowed as an adjective. `GitHub Actions CI/CD pipeline` reads fine to a
 * human and scores once instead of three times.
 *
 * The heuristic: an occurrence is flagged when the text immediately either side
 * of it is another target keyword with no comma / "and" / "with" / "using"
 * between them, or when it is glued to a following bare noun that turns it into
 * an adjective ("Go RESTful APIs", "Azure DevOps"). Slash-joined pairs
 * ("TypeScript/JavaScript") are flagged outright.
 *
 * ponytail: string-level heuristic, no parser. It cannot tell "Google Cloud
 * Platform" (a real multi-word term) from a glued pair unless the multi-word
 * term is itself on the target list — which it is, in practice, because the
 * list is canonical. Upgrade to a longest-match tokeniser if false positives
 * show up in real output.
 */
const NOUN_GLUE = new Set([
  "pipeline",
  "pipelines",
  "api",
  "apis",
  "service",
  "services",
  "workflow",
  "workflows",
  "stack",
  "app",
  "apps",
  "application",
  "applications",
]);

const SEPARATORS = new Set(["and", "with", "using", "in", "on", "to", "over", "via", "or"]);

export function checkTokenBoundaries(out: ImproveResumeLlmOutput): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const targets = targetKeywords(out);
  if (targets.length === 0) return issues;

  for (const b of indexBullets(out).values()) {
    for (const t of targets) {
      // "TypeScript/JavaScript" — never acceptable, both sides are targets.
      // Checked separately because the slash makes this *not* a token-boundary
      // occurrence of either term, which is exactly what is wrong with it.
      for (const at of matchAll(b.text, t, true)) {
        const after = b.text.slice(at.end);
        if (!after.startsWith("/")) continue;
        const next = targetAt(targets, after.slice(1));
        if (next) issues.push(glued(b, `${t}/${next}`, `${t}, ${next}`));
      }

      for (const at of matchAll(b.text, t)) {
        const before = b.text.slice(0, at.start);
        const after = b.text.slice(at.end);

        const nextWord = /^\s+([A-Za-z0-9+#./-]+)/.exec(after)?.[1]?.replace(/[.,;:]+$/, "");
        if (!nextWord) continue;
        const lower = nextWord.toLowerCase();
        if (SEPARATORS.has(lower)) continue;

        // "GitHub CI/CD", "Azure DevOps", "Go RESTful APIs" — two target terms
        // with nothing between them.
        const next = targetAt(targets, after.trimStart());
        // ".NET" is the one conventional exception: "C# .NET" is the platform's
        // own name and is the recruiter's gold-standard spelling.
        if (next && !next.startsWith(".")) {
          issues.push(glued(b, `${t} ${next}`, `${t}, ${next}`));
          continue;
        }
        // "Go RESTful APIs" — target used as an adjective on a bare noun.
        if (NOUN_GLUE.has(lower) && !endsClause(before)) {
          issues.push(glued(b, `${t} ${nextWord}`, `${t}, ${nextWord}`));
        }
      }
    }
  }
  return issues;
}

function glued(b: Bullets, found: string, fix: string): ValidationIssue {
  return {
    rule: "V2",
    severity: "error",
    message: `"${found}" swallows a target term as an adjective — write "${fix}"`,
    where: `${b.section}/${b.item}/${b.bullet}`,
  };
}

/** True when the term already opens a clause, so the noun after it is its own. */
function endsClause(before: string): boolean {
  return /(^|[,;:.]\s*|\b(and|with|using)\s+)$/i.test(before);
}

/** The longest target term starting at position 0 of `text`, if any. */
function targetAt(targets: string[], text: string): string | null {
  const hit = targets
    .filter((t) => new RegExp(`^${escapeRegex(t)}(?![A-Za-z0-9+#-])`, "i").test(text))
    .sort((a, b) => b.length - a.length)[0];
  return hit ?? null;
}

function matchAll(
  text: string,
  keyword: string,
  ignoreRightBoundary = false,
): { start: number; end: number }[] {
  const re = ignoreRightBoundary
    ? new RegExp(`(?<![A-Za-z0-9+#./-])${escapeRegex(keyword.trim())}`, "gi")
    : new RegExp(keywordRegex(keyword).source, "gi");
  const out: { start: number; end: number }[] = [];
  for (const m of text.matchAll(re)) out.push({ start: m.index, end: m.index + m[0].length });
  return out;
}

/* ------------------------------------------------------------------ V5 */

// R5 — repetition across roles reads as a career, repetition inside one role
// reads as stuffing.
const PER_ROLE_MAX = { core: 2, secondary: 2, tail: 1 } as const;
const PER_RESUME = { core: [3, 6], secondary: [2, 4], tail: [1, 2] } as const;

export function checkFrequency(out: ImproveResumeLlmOutput): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const targets = out.target_profile?.keywords ?? [];
  if (targets.length === 0) return issues;

  const bullets = [...indexBullets(out).values()];

  for (const kw of targets) {
    if (!kw?.canonical) continue;
    const hits = bullets.filter((b) => containsKeyword(b.text, kw.canonical));
    const tier = kw.tier ?? "tail";

    // Per role / per project.
    const byItem = new Map<string, Bullets[]>();
    for (const h of hits) {
      const key = `${h.section}/${h.item}`;
      byItem.set(key, [...(byItem.get(key) ?? []), h]);
    }
    for (const [where, group] of byItem) {
      if (group.length > PER_ROLE_MAX[tier]) {
        issues.push({
          rule: "V5",
          severity: "error",
          message: `"${kw.canonical}" (${tier}) appears ${group.length}x in one role — max ${PER_ROLE_MAX[tier]}`,
          where,
        });
      }
      const adjacent = group.some((b, i) => i > 0 && b.bullet === group[i - 1]!.bullet + 1);
      if (adjacent) {
        issues.push({
          rule: "V5",
          severity: "error",
          message: `"${kw.canonical}" appears in adjacent bullets of the same role`,
          where,
        });
      }
    }

    // Per resume. Over budget is an error, under the floor is only a warning —
    // the candidate may genuinely not have the material, and that is a gap
    // question, not a broken rewrite.
    const [floor, ceil] = PER_RESUME[tier];
    if (hits.length > ceil) {
      issues.push({
        rule: "V5",
        severity: "error",
        message: `"${kw.canonical}" (${tier}) appears ${hits.length}x across the resume — max ${ceil}`,
      });
    } else if (hits.length > 0 && hits.length < floor) {
      // Zero hits is not an under-frequency problem, it is a missing keyword —
      // already reported by `coverage.missing` with a reason. Warning about it
      // here fired on every keyword the candidate simply does not have (AWS,
      // C# .NET) and buried the keywords that were placed but under-used.
      issues.push({
        rule: "V5",
        severity: "warning",
        message: `"${kw.canonical}" (${tier}) appears ${hits.length}x across the resume — aim for ${floor}`,
      });
    }
  }
  return issues;
}

/* ------------------------------------------------------------------ V6 */

const PROJECT_BULLETS = 2;
const SHORT_ROLE_BULLETS = 3;
const LONG_ROLE_BULLETS = 6;

export function checkBulletBudget(out: ImproveResumeLlmOutput): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const doc = out?.optimized_resume;
  if (!doc) return issues;

  doc.experience?.forEach((role, i) => {
    const n = role?.bullets?.length ?? 0;
    const months = tenureMonths(role?.date_start ?? null, role?.date_end ?? null);
    const isIntern = /intern/i.test(role?.title ?? "");
    // Unknown tenure falls back to the permissive ceiling: dates are free-form
    // and often absent, and flagging on a failed parse punishes a good rewrite.
    const max =
      isIntern || (months !== null && months <= 6) ? SHORT_ROLE_BULLETS : LONG_ROLE_BULLETS;
    if (n > max) {
      issues.push({
        rule: "V6",
        severity: "error",
        message: `${n} bullets for a role with ${months === null ? "unknown tenure" : `${months} month(s)`} — max ${max}`,
        where: `experience/${i}`,
      });
    }
  });

  doc.projects?.forEach((p, i) => {
    const n = p?.bullets?.length ?? 0;
    if (n > PROJECT_BULLETS) {
      issues.push({
        rule: "V6",
        severity: "error",
        message: `${n} bullets on a project — max ${PROJECT_BULLETS}`,
        where: `projects/${i}`,
      });
    }
  });

  return issues;
}

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

/**
 * Tenure in months, or null when either end cannot be read.
 *
 * Resume dates are whatever the source PDF said: "Sep 2025", "09/2025",
 * "September 2025", "2025", "Present", "Current", null. Tolerant on purpose —
 * a null answer means "do not judge this role", which is the safe direction.
 */
export function tenureMonths(start: string | null, end: string | null): number | null {
  const a = parseMonth(start);
  if (a === null) return null;
  const b = /present|current|now|ongoing/i.test(end ?? "") ? nowIndex() : parseMonth(end);
  if (b === null) return null;
  // Inclusive: Jan–Mar is 3 months worked, not 2.
  return Math.max(0, b - a + 1);
}

/** Absolute month index (year*12 + month), or null. */
function parseMonth(s: string | null): number | null {
  if (!s) return null;
  const text = s.toLowerCase();
  const year = /(19|20)\d{2}/.exec(text)?.[0];
  if (!year) return null;

  const named = MONTHS.findIndex((m) => new RegExp(`\\b${m}`).test(text));
  if (named >= 0) return Number(year) * 12 + named;

  // Numeric forms: 09/2025, 2025-09.
  const numeric =
    /\b(\d{1,2})[/.-](?:19|20)\d{2}\b/.exec(text)?.[1] ??
    /\b(?:19|20)\d{2}[/.-](\d{1,2})\b/.exec(text)?.[1];
  const mo = numeric ? Number(numeric) : NaN;
  if (mo >= 1 && mo <= 12) return Number(year) * 12 + (mo - 1);

  // Year only — treat as January, which makes a bare "2024 - 2024" span read as
  // one month. Deliberately conservative: it only ever tightens the budget for
  // a role that gave no month at all.
  return Number(year) * 12;
}

function nowIndex(): number {
  const d = new Date();
  return d.getFullYear() * 12 + d.getMonth();
}

/* --------------------------------------------------------------- shared */

function indexBullets(out: ImproveResumeLlmOutput): Map<string, Bullets> {
  const map = new Map<string, Bullets>();
  const doc = out?.optimized_resume;
  if (!doc) return map;

  doc.experience?.forEach((role, item) => {
    role?.bullets?.forEach((text, bullet) => {
      if (typeof text === "string") {
        map.set(`experience/${item}/${bullet}`, { section: "experience", item, bullet, text });
      }
    });
  });
  doc.projects?.forEach((p, item) => {
    p?.bullets?.forEach((text, bullet) => {
      if (typeof text === "string") {
        map.set(`projects/${item}/${bullet}`, { section: "projects", item, bullet, text });
      }
    });
  });
  return map;
}

function targetKeywords(out: ImproveResumeLlmOutput): string[] {
  const from = out.target_profile?.keywords ?? [];
  return [...new Set(from.map((k) => k?.canonical).filter((k): k is string => !!k))];
}
